/**
 * Reservoir & Forecast (§7.1, view 2).
 *
 * Elevation trend against the rule curve, the inflow forecast fan, storage and surcharge
 * remaining, snowpack by band, and the what-if planner — which runs the player's proposed schedule
 * against the forecast median *and* the 90th percentile, because hedging against the band rather
 * than the median is the whole lesson of E19.
 */

import { useState } from 'react';
import { useSession } from '../../store/session';
import { useEngine } from '../useEngine';
import { getSite } from '../../sites';
import { Trend } from '../charts/Trend';
import { Readout } from '../widgets/Readout';
import { Gloss } from '../glossary/GlossaryDrawer';
import { flow, money, volume } from '../format';

export function ReservoirForecast(): JSX.Element {
  const snap = useSession((s) => s.snapshot);
  const site = getSite(useSession((s) => s.siteId));
  const settings = useSession((s) => s.settings);
  const whatIf = useSession((s) => s.whatIf);
  const whatIfPending = useSession((s) => s.whatIfPending);
  const engine = useEngine();
  const [planMW, setPlanMW] = useState(700);

  if (!snap) return <div style={{ padding: 24 }}>—</div>;

  const h = snap.history;
  const fc = snap.forecast;
  const nowMin = snap.simMinute;

  const leadX = fc ? fc.leadH.map((t) => nowMin + (t + (nowMin - fc.issuedAt) / 60 - (nowMin - fc.issuedAt) / 60) * 60) : [];
  const fcX = fc ? fc.leadH.map((t) => fc.issuedAt + t * 60) : [];
  void leadX;

  return (
    <div style={{ padding: 'var(--s3)', display: 'grid', gap: 'var(--s3)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 'var(--s2)' }}>
        <Readout label="Headwater" value={snap.headwater.toFixed(2)} unit="m" />
        <Readout label="Rule curve" value={snap.ruleCurveTarget.toFixed(2)} unit="m" size="sm" />
        <Readout
          label="Live storage"
          value={volume(snap.storage, settings).value}
          unit={volume(snap.storage, settings).unit}
          size="sm"
        />
        <Readout
          label="Surcharge remaining"
          value={volume(snap.surchargeRemaining, settings).value}
          unit={volume(snap.surchargeRemaining, settings).unit}
          size="sm"
          tone={snap.surchargeRemaining < 0.1e9 ? 'warn' : 'dim'}
        />
        <Readout label="Freeboard to crest" value={snap.freeboardToCrest.toFixed(2)} unit="m" size="sm" />
        <Readout
          label="Transducer"
          value={snap.selectedTransducer.toUpperCase()}
          size="sm"
          tone={Math.abs(snap.headwaterA - snap.headwaterB) > 0.25 ? 'warn' : 'dim'}
          hint={`A ${snap.headwaterA.toFixed(3)} m · B ${snap.headwaterB.toFixed(3)} m`}
        />
      </div>

      {Math.abs(snap.headwaterA - snap.headwaterB) > 0.25 && (
        <div className="panel" style={{ borderColor: 'var(--warn)' }}>
          <div className="label" style={{ color: 'var(--warn)' }}>
            ▲ Headwater transducers disagree
          </div>
          <div style={{ marginTop: 4 }}>
            A reads <span className="num">{snap.headwaterA.toFixed(3)} m</span>, B reads{' '}
            <span className="num">{snap.headwaterB.toFixed(3)} m</span>. Compare computed inflow
            (<span className="num">{Math.round(snap.inflowComputed)} m³/s</span>) against the gauged
            value (<span className="num">{Math.round(snap.inflowGauged)} m³/s</span>): the level
            sensor is the one that makes them diverge while gates and units are steady.
          </div>
          <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
            <SelectTransducer which="a" active={snap.selectedTransducer === 'a'} />
            <SelectTransducer which="b" active={snap.selectedTransducer === 'b'} />
          </div>
        </div>
      )}

      <section className="panel">
        <div className="label" style={{ marginBottom: 6 }}>
          Elevation against the <Gloss id="rule-curve">rule curve</Gloss>
        </div>
        <Trend
          height={190}
          yLabel="m ASL"
          fmt={(v) => v.toFixed(1)}
          series={[{ x: h.minuteT, y: h.headwater, colour: 'var(--water-shallow)', width: 2, label: 'headwater' }]}
          thresholds={[
            { value: site.dam.fsl, colour: 'var(--ink-dim)', label: `FSL ${site.dam.fsl}` },
            { value: site.dam.mfl, colour: 'var(--warn)', label: `MFL ${site.dam.mfl}` },
            { value: site.dam.mol, colour: 'var(--ink-dim)', label: `MOL ${site.dam.mol}` },
            { value: snap.ruleCurveTarget, colour: 'var(--ok)', label: 'rule curve', dash: [6, 4] },
          ]}
        />
      </section>

      <section className="panel">
        <div className="label" style={{ marginBottom: 6 }}>
          Inflow — observed and forecast (10–90 % band)
        </div>
        <Trend
          height={210}
          yLabel="m³/s"
          fmt={(v) => Math.round(v).toLocaleString('en-US')}
          marker={{ x: nowMin, label: 'now' }}
          bands={
            fc
              ? [{ x: fcX, lo: fc.p10, hi: fc.p90, colour: 'rgba(31,95,139,0.28)', label: '10–90 %' }]
              : []
          }
          series={[
            { x: h.minuteT, y: h.inflow, colour: 'var(--water-shallow)', width: 2, label: 'gauged' },
            { x: h.minuteT, y: h.outflow, colour: 'var(--ink-dim)', width: 1.2, label: 'release' },
            ...(fc ? [{ x: fcX, y: fc.median, colour: 'var(--ok)', width: 1.6, dash: [5, 3], label: 'forecast median' }] : []),
          ]}
        />
        {fc && (
          <div style={{ color: 'var(--ink-dim)', fontSize: 12, marginTop: 4 }} className="num">
            Issued at minute {fc.issuedAt.toLocaleString('en-US')} · 9 members · 120 h horizon. Spread
            grows with lead time; the band, not the median, is the plan.
          </div>
        )}
      </section>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 'var(--s3)' }}>
        <section className="panel">
          <div className="label" style={{ marginBottom: 6 }}>
            Snowpack by elevation band
          </div>
          {snap.swe.map((mm, i) => {
            const mid = site.hydrology.bandElevations[i] ?? 0;
            const weight = site.hydrology.bandWeights[i] ?? 0;
            const pct = Math.min(100, (mm / 1000) * 100);
            return (
              <div key={i} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }} className="num">
                  <span style={{ color: 'var(--ink-dim)' }}>
                    {mid} m · {(weight * 100).toFixed(0)}% of basin
                  </span>
                  <span>
                    {Math.round(mm)}
                    <span className="unit">mm SWE</span>
                  </span>
                </div>
                <div style={{ height: 6, background: 'var(--surface-2)', borderRadius: 3, marginTop: 3 }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: 'var(--water-shallow)', borderRadius: 3 }} />
                </div>
              </div>
            );
          })}
          <div style={{ color: 'var(--ink-dim)', fontSize: 12 }}>
            Snowline at <span className="num">{Math.round(snap.snowline)} m</span>. Melt scales with
            the fraction of each band still snow-covered, so a thin pack yields less than a deep one
            at the same temperature.
          </div>
        </section>

        <section className="panel">
          <div className="label" style={{ marginBottom: 6 }}>
            What-if planner
          </div>
          <div style={{ color: 'var(--ink-dim)', marginBottom: 8 }}>
            Runs a flat MW plan forward against the forecast median and the 90th percentile, using
            the same physics the plant does. Nothing here touches the live simulation.
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 10 }}>
            <label style={{ flex: 1 }}>
              <div className="label">Plant MW</div>
              <input
                type="number"
                className="num"
                value={planMW}
                onChange={(e) => setPlanMW(Number(e.target.value) || 0)}
              />
            </label>
            <button
              className="primary"
              disabled={whatIfPending}
              onClick={() =>
                engine.whatIf(
                  [50 as const, 90 as const].map((p) => ({
                    schedule: new Array<number>(48).fill(planMW),
                    horizonH: 72,
                    percentile: p,
                    autoSpill: snap.control.autoSpillArmed,
                    autoSpillMaxOpening: snap.control.autoSpillMaxOpening,
                  })),
                )
              }
            >
              {whatIfPending ? 'Running…' : 'Run 72 h'}
            </button>
          </div>

          {whatIf && whatIf.length > 0 && (
            <>
              <Trend
                height={160}
                yLabel="m ASL"
                fmt={(v) => v.toFixed(1)}
                series={whatIf.map((t) => ({
                  x: t.t,
                  y: t.headwater,
                  colour: t.percentile === 90 ? 'var(--warn)' : 'var(--water-shallow)',
                  width: 1.8,
                  label: `p${t.percentile}`,
                }))}
                thresholds={[
                  { value: site.dam.fsl, colour: 'var(--ink-dim)', label: 'FSL' },
                  { value: site.dam.mfl, colour: 'var(--alarm)', label: 'MFL' },
                ]}
              />
              <div className="table-scroll">
              <table style={{ width: '100%', minWidth: 460, marginTop: 8, fontSize: 12 }} className="num">
                <thead>
                  <tr style={{ color: 'var(--ink-dim)', textAlign: 'left' }}>
                    <th>Quantile</th>
                    <th>Peak pool</th>
                    <th>Peak gauge</th>
                    <th>Net</th>
                    <th>Breach</th>
                  </tr>
                </thead>
                <tbody>
                  {whatIf.map((t) => (
                    <tr key={t.percentile}>
                      <td>p{t.percentile}</td>
                      <td>{t.peakHeadwater.toFixed(2)} m</td>
                      <td>{Math.round(t.peakGauge).toLocaleString('en-US')} m³/s</td>
                      <td style={{ color: t.finalNet < 0 ? 'var(--alarm)' : 'var(--ink)' }}>{money(t.finalNet)}</td>
                      <td style={{ color: t.breachedMfl ? 'var(--alarm)' : t.breachedFsl ? 'var(--warn)' : 'var(--ok)' }}>
                        {t.breachedMfl ? 'MFL' : t.breachedFsl ? 'FSL' : t.breachedFloodStage ? 'flood stage' : 'none'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          )}
        </section>
      </div>

      <section className="panel">
        <div className="label" style={{ marginBottom: 6 }}>
          Five-day outlook
        </div>
        {fc ? (
          <Trend
            height={140}
            yLabel="°C / mm·h⁻¹"
            fmt={(v) => v.toFixed(1)}
            series={[
              { x: fcX.slice(0, 120), y: fc.tempOutlook.slice(0, 120), colour: 'var(--warn)', width: 1.5, label: 'temperature °C' },
              { x: fcX.slice(0, 120), y: fc.precipOutlook.slice(0, 120), colour: 'var(--water-shallow)', width: 1.5, label: 'precip mm/h' },
            ]}
          />
        ) : (
          <div style={{ color: 'var(--ink-dim)' }}>Awaiting the first issuance.</div>
        )}
        <div className="num" style={{ color: 'var(--ink-dim)', fontSize: 12, marginTop: 4 }}>
          Current inflow {flow(snap.inflowGauged, settings).value} {flow(snap.inflowGauged, settings).unit} gauged,{' '}
          {flow(snap.inflowComputed, settings).value} computed from level.
        </div>
      </section>
    </div>
  );
}

function SelectTransducer({ which, active }: { which: 'a' | 'b'; active: boolean }): JSX.Element {
  const dispatchFn = useEngine().dispatch;
  return (
    <button className={active ? 'primary' : undefined} onClick={() => dispatchFn({ t: 'select-transducer', which })}>
      Select transducer {which.toUpperCase()}
    </button>
  );
}
